import { useEffect, useState, useMemo, useCallback, ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useModal } from "../../hooks/useModal";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import PageMeta from "../../components/common/PageMeta";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import Button from "../../components/ui/button/Button";
import Badge from "../../components/ui/badge/Badge";
import { Modal } from "../../components/ui/modal";
import {
  FilingStatus,
  FilingPortalData,
  PortalCredential,
  getFilingStatuses,
  syncFilings,
  getSyncStatus,
  getPortalCredentials,
  createPortalCredential,
  deletePortalCredential,
} from "../../lib/api/intelligence";
import { fetchAllFilings, Filing, moveFilingToWorkspace, deleteFilingDraft } from "../../lib/api/wells";
import { fetchWorkspaces, Workspace } from "../../lib/api/workspaces";

// ---- Types ----

interface UnifiedFiling {
  id: string;
  source: "draft" | "synced" | "submitted" | "manual";
  form_type: string;
  status: string;
  api10?: string;
  lease_name?: string;
  well_number?: string;
  last_updated: string;
  // tracker rows only — drives detail modal
  _tracker?: FilingStatus;
  // draft rows only — drives navigation and deletion
  draft_id?: string;       // filing UUID (for move-to-workspace)
  draft_route?: string | null; // computed navigation path for Continue
  delete_id?: string;      // ID passed to deleteFilingDraft
}

interface SyncAlert {
  type: "success" | "error";
  message: string;
}

// ---- Normalization ----

function normalizeDraft(f: Filing): UnifiedFiling {
  const ft = (f.form_type ?? '').toLowerCase().replace(/[-\s]/g, '');
  let draft_route: string | null = null;
  let delete_id: string = f.id;

  if (ft === 'w3' || ft === 'sundry') {
    const sessionId = f.metadata?.session_id as string | undefined;
    draft_route = sessionId ? `/w3-wizard/${sessionId}` : null;
    // f.id is the W3FormORM UUID
  } else {
    // W-3A and C-103 are both PlanSnapshot-backed
    const planId = f.metadata?.plan_id as string | undefined;
    draft_route = planId ? `/plan/${planId}` : null;
    delete_id = planId ?? f.id;
  }

  return {
    id: f.id,
    source: "draft",
    form_type: f.form_type,
    status: f.status,
    api10: f.api14 ? f.api14.slice(0, 10) : undefined,
    lease_name: f.lease_name,
    well_number: f.well_number,
    last_updated: f.updated_at,
    draft_id: f.id,
    draft_route,
    delete_id,
  };
}

function normalizeTracker(f: FilingStatus): UnifiedFiling {
  const portal = f.raw_portal_data;
  return {
    id: f.id,
    source: f.source,
    form_type: f.form_type,
    status: f.status,
    api10: portal?.api_number ? portal.api_number.slice(0, 10) : undefined,
    lease_name: portal?.lease_name,
    well_number: portal?.well_number,
    last_updated: f.status_date ?? f.created_at,
    _tracker: f,
  };
}

// ---- Helpers ----

const formatDate = (dateString: string | null | undefined) => {
  if (!dateString) return "—";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};


const formTypeBadgeColor = (
  formType: string
): "info" | "primary" | "warning" | "light" => {
  const n = formType?.toLowerCase();
  if (n === "w3a" || n === "w-3a") return "info";
  if (n === "w3" || n === "w-3") return "primary";
  if (n === "c103" || n === "c-103") return "warning";
  return "light";
};

const statusBadgeColor = (
  status: string
): "success" | "warning" | "info" | "error" => {
  switch (status) {
    case "approved":
      return "success";
    case "draft":
    case "pending":
      return "warning";
    case "under_review":
    case "submitted":
      return "info";
    case "rejected":
    case "deficiency":
    case "revision_requested":
      return "error";
    default:
      return "info";
  }
};

const statusLabel = (status: string) => {
  const map: Record<string, string> = {
    draft: "Draft",
    pending: "Pending",
    submitted: "Submitted",
    under_review: "Under Review",
    approved: "Approved",
    rejected: "Rejected",
    revision_requested: "Revision Requested",
    deficiency: "Deficiency",
    withdrawn: "Withdrawn",
    "revised and submitted": "Revised",
  };
  return map[status] ?? status.replace(/_/g, " ");
};

const sourceBadgeColor = (
  source: string
): "light" | "primary" | "dark" | "warning" => {
  switch (source) {
    case "synced":
      return "light";
    case "submitted":
      return "primary";
    case "manual":
      return "dark";
    case "draft":
      return "warning";
    default:
      return "light";
  }
};

const sourceLabel = (source: string) => {
  switch (source) {
    case "synced":
      return "RRC Synced";
    case "submitted":
      return "Submitted";
    case "manual":
      return "Manual";
    case "draft":
      return "Draft";
    default:
      return source;
  }
};

// ---- Status Filter Tabs ----

const STATUS_TABS = [
  { label: "All", value: null },
  { label: "Draft", value: "draft" },
  { label: "Active", value: "active" },
  { label: "Needs Attention", value: "attention" },
  { label: "Approved", value: "approved" },
] as const;

type TabValue = (typeof STATUS_TABS)[number]["value"];

const ACTIVE_STATUSES = new Set(["submitted", "under_review", "pending"]);
const ATTENTION_STATUSES = new Set([
  "rejected",
  "revision_requested",
  "deficiency",
]);

function matchesTab(item: UnifiedFiling, tab: TabValue): boolean {
  if (tab === null) return true;
  if (tab === "draft") return item.status === "draft";
  if (tab === "active") return ACTIVE_STATUSES.has(item.status);
  if (tab === "attention") return ATTENTION_STATUSES.has(item.status);
  if (tab === "approved") return item.status === "approved";
  return true;
}

// Map a raw status string (e.g. from ?status= URL param) to a tab value
function statusToTab(status: string | null): TabValue {
  if (!status) return null;
  if (status === "draft") return "draft";
  if (ACTIVE_STATUSES.has(status)) return "active";
  if (ATTENTION_STATUSES.has(status)) return "attention";
  if (status === "approved") return "approved";
  return null;
}

// ---- Portal Credentials Section ----

interface CredentialsSectionProps {
  credentials: PortalCredential[];
  onConnect: () => void;
  onDisconnect: (id: string) => void;
}

function PortalCredentialsSection({
  credentials,
  onConnect,
  onDisconnect,
}: CredentialsSectionProps) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03] mb-6">
      <div className="px-6 py-4">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="text-base font-semibold text-gray-800 dark:text-white/90">
            Portal Connections
          </h3>
          <Button size="sm" onClick={onConnect}>
            {credentials.length === 0 ? "Connect RRC Account" : "Connect Account"}
          </Button>
        </div>

        {credentials.length === 0 ? (
          <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
            Connect your RRC portal account to sync filings.
          </p>
        ) : (
          <div className="mt-3 divide-y divide-gray-100 dark:divide-white/[0.05]">
            {credentials.map((cred) => (
              <div
                key={cred.id}
                className="flex items-center justify-between py-3"
              >
                <div>
                  <span className="block text-sm font-medium text-gray-800 dark:text-white/90">
                    {cred.agency}
                  </span>
                  <span className="block text-xs text-gray-500 dark:text-gray-400">
                    {cred.username}
                    {cred.last_successful_login
                      ? ` · Last synced ${formatDate(cred.last_successful_login)}`
                      : " · Never synced"}
                  </span>
                </div>
                <button
                  onClick={() => onDisconnect(cred.id)}
                  className="ml-4 flex h-7 w-7 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-white/5 dark:hover:text-gray-300 transition-colors"
                  title="Disconnect"
                  aria-label={`Disconnect ${cred.agency} account`}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M6.04289 16.5413C5.65237 16.9318 5.65237 17.565 6.04289 17.9555C6.43342 18.346 7.06658 18.346 7.45711 17.9555L11.9987 13.4139L16.5408 17.956C16.9313 18.3466 17.5645 18.3466 17.955 17.956C18.3455 17.5655 18.3455 16.9323 17.955 16.5418L13.4129 11.9997L17.955 7.4576C18.3455 7.06707 18.3455 6.43391 17.955 6.04338C17.5645 5.65286 16.9313 5.65286 16.5408 6.04338L11.9987 10.5855L7.45711 6.0439C7.06658 5.65338 6.43342 5.65338 6.04289 6.0439C5.65237 6.43442 5.65237 7.06759 6.04289 7.45811L10.5845 11.9997L6.04289 16.5413Z"
                      fill="currentColor"
                    />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---- Connect Account Modal ----

interface ConnectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (
    agency: string,
    username: string,
    password: string,
    vaultPassphrase: string
  ) => Promise<void>;
  hasVaultPassphrase: boolean;
}

function ConnectAccountModal({
  isOpen,
  onClose,
  onSave,
  hasVaultPassphrase,
}: ConnectModalProps) {
  const [agency, setAgency] = useState("RRC");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [vaultPassphrase, setVaultPassphrase] = useState("");
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  const handleClose = () => {
    setAgency("RRC");
    setUsername("");
    setPassword("");
    setVaultPassphrase("");
    setModalError(null);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) {
      setModalError("Username and password are required.");
      return;
    }
    setSaving(true);
    setModalError(null);
    try {
      await onSave(agency, username, password, vaultPassphrase);
      handleClose();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to save credentials.";
      if (
        message.toLowerCase().includes("vault passphrase") ||
        message.toLowerCase().includes("invalid")
      ) {
        setModalError("Invalid vault passphrase. Please try again.");
      } else {
        setModalError(message);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} className="max-w-md mx-4">
      <div className="px-6 py-6 sm:px-8 sm:py-8">
        <h4 className="text-lg font-semibold text-gray-800 dark:text-white/90 mb-1">
          Connect Portal Account
        </h4>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
          Enter your agency portal credentials to enable filing sync.
        </p>

        {modalError && (
          <div className="mb-4 rounded-lg bg-error-50 p-3 dark:bg-error-500/15">
            <p className="text-sm text-error-700 dark:text-error-400">
              {modalError}
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Agency
            </label>
            <select
              value={agency}
              onChange={(e) => setAgency(e.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800"
            >
              <option value="RRC">RRC (Texas Railroad Commission)</option>
            </select>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Username
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Your RRC portal username"
              className="w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Your RRC portal password"
              className="w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Vault Passphrase{" "}
              {hasVaultPassphrase ? "(required)" : "(optional)"}
            </label>
            <input
              type="password"
              value={vaultPassphrase}
              onChange={(e) => setVaultPassphrase(e.target.value)}
              placeholder={
                hasVaultPassphrase
                  ? "Enter your vault passphrase"
                  : "Set a passphrase to protect credentials"
              }
              className="w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
            />
            <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
              {hasVaultPassphrase
                ? "Required to manage portal credentials."
                : "Optional. Once set, this passphrase will be required for all future credential changes."}
            </p>
          </div>

          <div className="flex gap-3 mt-2">
            <Button type="submit" disabled={saving} className="flex-1">
              {saving ? "Saving..." : "Save"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={saving}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
}

// ---- Filing Detail Modal ----

function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <>
      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
        {label}
      </dt>
      <dd className="text-sm text-gray-800 dark:text-white/90">{value || "—"}</dd>
    </>
  );
}

interface FilingDetailModalProps {
  filing: FilingStatus | null;
  isOpen: boolean;
  onClose: () => void;
}

function FilingDetailModal({ filing, isOpen, onClose }: FilingDetailModalProps) {
  if (!filing) return null;

  const portal = filing.raw_portal_data as FilingPortalData | undefined;

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="max-w-2xl mx-4">
      <div className="px-6 py-6 sm:px-8 sm:py-8">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h4 className="text-lg font-semibold text-gray-800 dark:text-white/90">
              Filing Detail
            </h4>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Tracking # {filing.filing_id}
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-6 overflow-y-auto max-h-[70vh] pr-1">
          <div>
            <h5 className="text-base font-semibold text-gray-800 dark:text-white/90 mb-3">
              Filing Information
            </h5>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-3">
              <DetailRow label="Tracking Number" value={filing.filing_id} />
              <DetailRow
                label="Form Type"
                value={
                  <Badge size="sm" color={formTypeBadgeColor(filing.form_type)}>
                    {filing.form_type || "Unknown"}
                  </Badge>
                }
              />
              <DetailRow
                label="Stage / Status"
                value={
                  portal?.stage ? (
                    <span className="text-sm text-gray-800 dark:text-white/90">
                      {portal.stage}
                    </span>
                  ) : (
                    <Badge size="sm" color={statusBadgeColor(filing.status)}>
                      {statusLabel(filing.status)}
                    </Badge>
                  )
                }
              />
              <DetailRow
                label="Source"
                value={
                  <Badge size="sm" color={sourceBadgeColor(filing.source)}>
                    {sourceLabel(filing.source)}
                  </Badge>
                }
              />
              <DetailRow label="Agency" value={filing.agency} />
              <DetailRow label="Last Modified" value={formatDate(filing.status_date)} />
              <DetailRow label="Date Created" value={formatDate(filing.created_at)} />
              {filing.portal_url && (
                <>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Portal Link
                  </dt>
                  <dd>
                    <a
                      href={filing.portal_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-brand-500 hover:underline break-all"
                    >
                      Open in Portal
                    </a>
                  </dd>
                </>
              )}
            </dl>
          </div>

          <div>
            <h5 className="text-base font-semibold text-gray-800 dark:text-white/90 mb-3">
              Well Information
            </h5>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-3">
              <DetailRow label="API Number" value={portal?.api_number} />
              <DetailRow label="Lease Name" value={portal?.lease_name} />
              <DetailRow label="Well Number" value={portal?.well_number} />
              <DetailRow label="Operator" value={portal?.operator} />
              <DetailRow label="Operator Number" value={portal?.operator_number} />
              <DetailRow label="Lease / Gas ID" value={portal?.lease_or_gas_id} />
            </dl>
          </div>

          <div>
            <h5 className="text-base font-semibold text-gray-800 dark:text-white/90 mb-3">
              Location
            </h5>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-3">
              <DetailRow label="State" value={filing.state} />
              <DetailRow label="District" value={filing.district || portal?.district} />
              <DetailRow label="County" value={filing.county || portal?.county} />
            </dl>
          </div>

          <div>
            <h5 className="text-base font-semibold text-gray-800 dark:text-white/90 mb-3">
              Regulatory
            </h5>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-3">
              <DetailRow label="Drilling Permit #" value={portal?.drilling_permit} />
              <DetailRow label="Rule 37 Case" value={portal?.rule_37_case} />
              <DetailRow label="Reviewer / Submitter" value={filing.reviewer_name} />
            </dl>
            {filing.agency_remarks && (
              <div className="mt-3">
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Remarks
                </dt>
                <dd className="text-sm text-gray-800 dark:text-white/90 whitespace-pre-wrap rounded-lg bg-gray-50 dark:bg-white/[0.03] px-4 py-3 border border-gray-100 dark:border-white/[0.05]">
                  {filing.agency_remarks}
                </dd>
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <Button variant="outline" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ---- Move to Workspace Modal ----

interface MoveToWorkspaceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onMove: (workspaceId: string) => Promise<void>;
}

function MoveToWorkspaceModal({
  isOpen,
  onClose,
  onMove,
}: MoveToWorkspaceModalProps) {
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [loadingWorkspaces, setLoadingWorkspaces] = useState(false);
  const [selectedId, setSelectedId] = useState<string>("");
  const [moving, setMoving] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedId("");
    setModalError(null);
    setLoadingWorkspaces(true);
    fetchWorkspaces({ is_active: true })
      .then((res) => setWorkspaces(res.results ?? []))
      .catch(() => setModalError("Failed to load workspaces."))
      .finally(() => setLoadingWorkspaces(false));
  }, [isOpen]);

  const handleClose = () => {
    setSelectedId("");
    setModalError(null);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedId) {
      setModalError("Please select a workspace.");
      return;
    }
    setMoving(true);
    setModalError(null);
    try {
      await onMove(selectedId);
      handleClose();
    } catch (err) {
      setModalError(
        err instanceof Error ? err.message : "Failed to move filing."
      );
    } finally {
      setMoving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} className="max-w-sm mx-4">
      <div className="px-6 py-6 sm:px-8 sm:py-8">
        <h4 className="text-lg font-semibold text-gray-800 dark:text-white/90 mb-1">
          Move to Workspace
        </h4>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
          Select a workspace to reassign this draft filing.
        </p>

        {modalError && (
          <div className="mb-4 rounded-lg bg-error-50 p-3 dark:bg-error-500/15">
            <p className="text-sm text-error-700 dark:text-error-400">
              {modalError}
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Workspace
            </label>
            {loadingWorkspaces ? (
              <div className="flex items-center gap-2 py-2 text-sm text-gray-500 dark:text-gray-400">
                <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-brand-500 border-r-transparent" />
                Loading workspaces...
              </div>
            ) : (
              <select
                value={selectedId}
                onChange={(e) => setSelectedId(e.target.value)}
                className="w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800"
              >
                <option value="">— Select workspace —</option>
                {workspaces.map((ws) => (
                  <option key={ws.id} value={ws.id}>
                    {ws.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="flex gap-3 mt-2">
            <Button type="submit" disabled={moving || loadingWorkspaces} className="flex-1">
              {moving ? "Moving..." : "Move"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={moving}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
}

// ---- Main Page ----

export default function Filings() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Unified data
  const [items, setItems] = useState<UnifiedFiling[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Credentials
  const [credentials, setCredentials] = useState<PortalCredential[]>([]);
  const [credsLoading, setCredsLoading] = useState(true);
  const [hasVaultPassphrase, setHasVaultPassphrase] = useState(false);

  // UI state
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState<TabValue>(
    statusToTab(searchParams.get("status"))
  );
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;

  // Sync state
  const [syncing, setSyncing] = useState(false);
  const [syncAlert, setSyncAlert] = useState<SyncAlert | null>(null);

  // Modal state
  const [connectModalOpen, setConnectModalOpen] = useState(false);
  const [detailFiling, setDetailFiling] = useState<FilingStatus | null>(null);
  const [disconnectingCredId, setDisconnectingCredId] = useState<string | null>(null);
  const [disconnectDeleting, setDisconnectDeleting] = useState(false);
  const disconnectModal = useModal();

  // Move to workspace state
  const [moveFilingId, setMoveFilingId] = useState<string | null>(null);
  const [moveAlert, setMoveAlert] = useState<SyncAlert | null>(null);

  // Delete draft state
  const [pendingDelete, setPendingDelete] = useState<{ id: string; formType: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  // ---- Data Loaders ----

  const loadFilings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [draftsRes, trackerRes] = await Promise.allSettled([
        fetchAllFilings({ page: 1, page_size: 500, ordering: "-updated_at", status: ["draft"] }),
        getFilingStatuses({ page_size: "500" }),
      ]);

      // Only include internal filings that are still in draft — once submitted,
      // the FilingStatus tracker record is the source of truth for that filing.
      const drafts: UnifiedFiling[] =
        draftsRes.status === "fulfilled"
          ? (draftsRes.value.filings ?? [])
              .filter((f) => f.status === "draft")
              .map(normalizeDraft)
          : [];

      const trackerRaw: FilingStatus[] =
        trackerRes.status === "fulfilled"
          ? Array.isArray(trackerRes.value)
            ? trackerRes.value
            : (
                trackerRes.value as unknown as {
                  results: FilingStatus[];
                }
              ).results ?? []
          : [];

      const tracker: UnifiedFiling[] = trackerRaw.map(normalizeTracker);

      const merged = [...drafts, ...tracker].sort(
        (a, b) =>
          new Date(b.last_updated).getTime() -
          new Date(a.last_updated).getTime()
      );

      setItems(merged);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load filings."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCredentials = useCallback(async () => {
    try {
      setCredsLoading(true);
      const response = await getPortalCredentials();
      setCredentials(response.credentials ?? []);
      setHasVaultPassphrase(response.has_vault_passphrase ?? false);
    } catch {
      setCredentials([]);
    } finally {
      setCredsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFilings();
    loadCredentials();
  }, [loadFilings, loadCredentials]);

  // ---- Credential Management ----

  const handleConnect = async (
    agency: string,
    username: string,
    password: string,
    vaultPassphrase: string
  ) => {
    await createPortalCredential({
      agency,
      username,
      password,
      vault_passphrase: vaultPassphrase || undefined,
    });
    await loadCredentials();
  };

  const handleDisconnect = (id: string) => {
    setDisconnectingCredId(id);
    disconnectModal.openModal();
  };

  const handleDisconnectConfirm = async () => {
    if (!disconnectingCredId) return;
    setDisconnectDeleting(true);
    try {
      await deletePortalCredential(disconnectingCredId);
      setCredentials((prev) => prev.filter((c) => c.id !== disconnectingCredId));
      disconnectModal.closeModal();
    } finally {
      setDisconnectDeleting(false);
    }
  };

  // ---- Sync Flow ----

  const handleSync = useCallback(async () => {
    setSyncing(true);
    setSyncAlert(null);
    try {
      const { task_id } = await syncFilings("RRC");

      const poll = async (): Promise<void> => {
        const status = await getSyncStatus(task_id);
        if (status.status === "success") {
          const r = status.result;
          const summary = r
            ? `Synced ${r.total_scraped} filings: ${r.created} new, ${r.updated} updated`
            : "Sync complete.";
          setSyncAlert({ type: "success", message: summary });
          await loadFilings();
          setSyncing(false);
        } else if (status.status === "failure") {
          setSyncAlert({
            type: "error",
            message: status.error ?? "Sync failed. Please try again.",
          });
          setSyncing(false);
        } else {
          await new Promise((res) => setTimeout(res, 3000));
          return poll();
        }
      };

      await poll();
    } catch (err) {
      setSyncAlert({
        type: "error",
        message:
          err instanceof Error ? err.message : "Sync failed. Please try again.",
      });
      setSyncing(false);
    }
  }, [loadFilings]);

  // ---- Move to Workspace ----

  const handleMoveToWorkspace = useCallback(
    async (workspaceId: string) => {
      if (!moveFilingId) return;
      await moveFilingToWorkspace(moveFilingId, workspaceId);
      setMoveAlert({ type: "success", message: "Filing moved to workspace." });
      await loadFilings();
    },
    [moveFilingId, loadFilings]
  );

  // ---- Delete Draft ----

  const handleDeleteConfirm = useCallback(async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await deleteFilingDraft(pendingDelete.id, pendingDelete.formType);
      setPendingDelete(null);
      await loadFilings();
    } catch (err) {
      console.error("[Filings] Delete error:", err);
    } finally {
      setDeleting(false);
    }
  }, [pendingDelete, loadFilings]);

  // ---- Tab counts ----

  const tabCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const tab of STATUS_TABS) {
      if (tab.value === null) {
        counts["all"] = items.length;
      } else {
        counts[tab.value] = items.filter((item) =>
          matchesTab(item, tab.value as TabValue)
        ).length;
      }
    }
    return counts;
  }, [items]);

  // ---- Filter + Search + Pagination ----

  const filteredItems = useMemo(() => {
    let result = items.filter((item) => matchesTab(item, activeTab));
    if (!searchTerm.trim()) return result;
    const lower = searchTerm.toLowerCase();
    return result.filter(
      (item) =>
        item.lease_name?.toLowerCase().includes(lower) ||
        item.api10?.toLowerCase().includes(lower) ||
        item.form_type?.toLowerCase().includes(lower) ||
        item.status?.toLowerCase().includes(lower) ||
        item._tracker?.filing_id?.toLowerCase().includes(lower) ||
        item._tracker?.raw_portal_data?.operator?.toLowerCase().includes(lower) ||
        item._tracker?.county?.toLowerCase().includes(lower) ||
        item._tracker?.agency?.toLowerCase().includes(lower)
    );
  }, [items, activeTab, searchTerm]);

  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentItems = filteredItems.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);

  const hasCredentials = credentials.length > 0;

  // ---- Render ----

  return (
    <>
      <PageMeta
        title="Filings | RegulAgent"
        description="All filings — drafts and agency-synced records"
      />
      <PageBreadcrumb pageTitle="Filings" />

      {/* Portal Credentials */}
      {!credsLoading && (
        <PortalCredentialsSection
          credentials={credentials}
          onConnect={() => setConnectModalOpen(true)}
          onDisconnect={handleDisconnect}
        />
      )}

      {/* Connect Account Modal */}
      <ConnectAccountModal
        isOpen={connectModalOpen}
        onClose={() => setConnectModalOpen(false)}
        onSave={handleConnect}
        hasVaultPassphrase={hasVaultPassphrase}
      />

      {/* Filing Detail Modal */}
      <FilingDetailModal
        filing={detailFiling}
        isOpen={!!detailFiling}
        onClose={() => setDetailFiling(null)}
      />

      {/* Move to Workspace Modal */}
      <MoveToWorkspaceModal
        isOpen={!!moveFilingId}
        onClose={() => setMoveFilingId(null)}
        onMove={handleMoveToWorkspace}
      />

      {/* Disconnect Account Confirmation Modal */}
      <Modal
        isOpen={disconnectModal.isOpen}
        onClose={disconnectModal.closeModal}
        className="max-w-md mx-4"
      >
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100 dark:bg-red-500/20">
              <svg className="h-5 w-5 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 dark:text-white/90">
              Disconnect Account?
            </h4>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            Are you sure you want to disconnect this portal account? Your synced filings will remain, but automatic sync will stop until you reconnect.
          </p>
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={disconnectModal.closeModal}
              disabled={disconnectDeleting}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleDisconnectConfirm}
              disabled={disconnectDeleting}
              className="bg-red-600 hover:bg-red-700 text-white border-red-600"
            >
              {disconnectDeleting ? "Disconnecting..." : "Disconnect"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Draft Confirmation Modal */}
      <Modal
        isOpen={!!pendingDelete}
        onClose={() => !deleting && setPendingDelete(null)}
        className="max-w-md mx-4"
      >
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100 dark:bg-red-500/20">
              <svg className="h-5 w-5 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 dark:text-white/90">
              Delete Draft?
            </h4>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            This draft filing will be permanently deleted. This action cannot be undone.
          </p>
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPendingDelete(null)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleDeleteConfirm}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white border-red-600"
            >
              {deleting ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Main table card */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-r-transparent" />
            <p className="mt-3 text-gray-600 dark:text-gray-400">
              Loading filings...
            </p>
          </div>
        </div>
      ) : error ? (
        <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
          <div className="p-6">
            <div className="rounded-lg bg-error-50 p-4 dark:bg-error-500/15">
              <p className="text-sm font-medium text-error-700 dark:text-error-400">
                {error}
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
          {/* Table header */}
          <div className="px-6 py-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                  Filings
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {filteredItems.length} of {items.length} filings
                </p>
              </div>

              <div className="flex flex-col gap-3 sm:items-end">
                {/* Sync alert */}
                {syncAlert && (
                  <div
                    className={`rounded-lg px-4 py-2 text-sm font-medium ${
                      syncAlert.type === "success"
                        ? "bg-success-50 text-success-700 dark:bg-success-500/15 dark:text-success-400"
                        : "bg-error-50 text-error-700 dark:bg-error-500/15 dark:text-error-400"
                    }`}
                  >
                    {syncAlert.message}
                    <button
                      onClick={() => setSyncAlert(null)}
                      className="ml-3 opacity-60 hover:opacity-100 transition-opacity"
                      aria-label="Dismiss"
                    >
                      ×
                    </button>
                  </div>
                )}

                {/* Move-to-workspace alert */}
                {moveAlert && (
                  <div
                    className={`rounded-lg px-4 py-2 text-sm font-medium ${
                      moveAlert.type === "success"
                        ? "bg-success-50 text-success-700 dark:bg-success-500/15 dark:text-success-400"
                        : "bg-error-50 text-error-700 dark:bg-error-500/15 dark:text-error-400"
                    }`}
                  >
                    {moveAlert.message}
                    <button
                      onClick={() => setMoveAlert(null)}
                      className="ml-3 opacity-60 hover:opacity-100 transition-opacity"
                      aria-label="Dismiss"
                    >
                      ×
                    </button>
                  </div>
                )}

                <div className="flex items-center gap-3">
                  {/* New Filing button */}
                  <Button
                    size="sm"
                    onClick={() => navigate("/filings/new")}
                  >
                    + New Filing
                  </Button>

                  {/* Sync button */}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleSync}
                    disabled={!hasCredentials || syncing}
                    startIcon={
                      syncing ? (
                        <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-brand-500 border-r-transparent" />
                      ) : (
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M4 12C4 7.58172 7.58172 4 12 4C14.5264 4 16.7793 5.12399 18.2995 6.90466M20 12C20 16.4183 16.4183 20 12 20C9.47357 20 7.22067 18.876 5.70053 17.0953"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                          <path
                            d="M19 3V7H15"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M5 21V17H9"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )
                    }
                  >
                    {syncing ? "Syncing..." : "Sync RRC"}
                  </Button>

                  {/* Search */}
                  <div className="relative">
                    <button
                      type="button"
                      className="absolute -translate-y-1/2 left-4 top-1/2"
                      tabIndex={-1}
                    >
                      <svg
                        className="fill-gray-500 dark:fill-gray-400"
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M3.04199 9.37381C3.04199 5.87712 5.87735 3.04218 9.37533 3.04218C12.8733 3.04218 15.7087 5.87712 15.7087 9.37381C15.7087 12.8705 12.8733 15.7055 9.37533 15.7055C5.87735 15.7055 3.04199 12.8705 3.04199 9.37381ZM9.37533 1.54218C5.04926 1.54218 1.54199 5.04835 1.54199 9.37381C1.54199 13.6993 5.04926 17.2055 9.37533 17.2055C11.2676 17.2055 13.0032 16.5346 14.3572 15.4178L17.1773 18.2381C17.4702 18.531 17.945 18.5311 18.2379 18.2382C18.5308 17.9453 18.5309 17.4704 18.238 17.1775L15.4182 14.3575C16.5367 13.0035 17.2087 11.2671 17.2087 9.37381C17.2087 5.04835 13.7014 1.54218 9.37533 1.54218Z"
                          fill=""
                        />
                      </svg>
                    </button>
                    <input
                      type="text"
                      placeholder="Search filings..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="h-[42px] w-full rounded-lg border border-gray-300 bg-transparent py-2.5 pl-[42px] pr-4 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800 xl:w-[260px]"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Status filter tabs */}
          <div className="px-6 pb-4 border-t border-gray-100 dark:border-white/[0.05] pt-4">
            <div className="inline-flex items-center shadow-theme-xs">
              {STATUS_TABS.map((tab, i) => {
                const count =
                  tab.value === null
                    ? tabCounts["all"]
                    : tabCounts[tab.value] ?? 0;
                const isActive = activeTab === tab.value;
                return (
                  <button
                    key={String(tab.value)}
                    type="button"
                    onClick={() => {
                      setActiveTab(isActive ? null : (tab.value as TabValue));
                      setCurrentPage(1);
                    }}
                    className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium ring-1 ring-inset ring-brand-500 transition-colors
                      ${i === 0 ? "rounded-l-lg" : "-ml-px"}
                      ${i === STATUS_TABS.length - 1 ? "rounded-r-lg" : ""}
                      ${
                        isActive
                          ? "bg-brand-500 text-white"
                          : "bg-transparent text-brand-500 hover:bg-brand-500 hover:text-white"
                      }`}
                  >
                    {tab.label}
                    {count > 0 && (
                      <span
                        className={`inline-flex items-center justify-center rounded-full px-1.5 py-0.5 text-xs font-semibold leading-none ${
                          isActive
                            ? "bg-white/20 text-white"
                            : "bg-brand-100 text-brand-700 dark:bg-brand-500/20 dark:text-brand-300"
                        }`}
                      >
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Table */}
          {items.length === 0 ? (
            <div className="p-6 text-center border-t border-gray-100 dark:border-white/[0.05]">
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                No filings yet.
              </p>
              {!hasCredentials && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Connect a portal account above and click "Sync RRC" to sync
                  filings from the agency portal.
                </p>
              )}
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="p-6 text-center border-t border-gray-100 dark:border-white/[0.05]">
              <p className="text-gray-500 dark:text-gray-400">
                No filings match the current filter.
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-hidden">
                <div className="max-w-full overflow-x-auto">
                  <Table>
                    <TableHeader className="border-gray-100 border-y dark:border-white/[0.05]">
                      <TableRow>
                        <TableCell
                          isHeader
                          className="py-3 px-6 font-normal text-gray-500 text-start text-theme-sm dark:text-gray-400"
                        >
                          Well / Lease
                        </TableCell>
                        <TableCell
                          isHeader
                          className="px-6 py-3 font-normal text-gray-500 text-start text-theme-sm dark:text-gray-400"
                        >
                          API
                        </TableCell>
                        <TableCell
                          isHeader
                          className="px-6 py-3 font-normal text-gray-500 text-start text-theme-sm dark:text-gray-400"
                        >
                          Form Type
                        </TableCell>
                        <TableCell
                          isHeader
                          className="px-6 py-3 font-normal text-gray-500 text-start text-theme-sm dark:text-gray-400"
                        >
                          Status
                        </TableCell>
                        <TableCell
                          isHeader
                          className="px-6 py-3 font-normal text-gray-500 text-start text-theme-sm dark:text-gray-400"
                        >
                          Source
                        </TableCell>
                        <TableCell
                          isHeader
                          className="px-6 py-3 font-normal text-gray-500 text-start text-theme-sm dark:text-gray-400"
                        >
                          Tracking #
                        </TableCell>
                        <TableCell
                          isHeader
                          className="px-6 py-3 font-normal text-gray-500 text-start text-theme-sm dark:text-gray-400"
                        >
                          Last Updated
                        </TableCell>
                        <TableCell
                          isHeader
                          className="px-6 py-3 font-normal text-gray-500 text-start text-theme-sm dark:text-gray-400"
                        >
                          Action
                        </TableCell>
                      </TableRow>
                    </TableHeader>
                    <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                      {currentItems.map((item) => (
                        <TableRow key={`${item.source}-${item.id}`}>
                          {/* Well / Lease */}
                          <TableCell className="px-6 py-4">
                            <span className="block text-theme-sm font-medium text-gray-800 dark:text-gray-200">
                              {item.lease_name || "—"}
                            </span>
                            {item.well_number && (
                              <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                Well #{item.well_number}
                              </span>
                            )}
                          </TableCell>

                          {/* API */}
                          <TableCell className="px-6 py-4 whitespace-nowrap text-gray-700 text-theme-sm dark:text-gray-400">
                            {item.api10 || "—"}
                          </TableCell>

                          {/* Form Type */}
                          <TableCell className="px-6 py-4 whitespace-nowrap">
                            <Badge
                              size="sm"
                              color={formTypeBadgeColor(item.form_type)}
                            >
                              {item.form_type || "—"}
                            </Badge>
                          </TableCell>

                          {/* Status */}
                          <TableCell className="px-6 py-4 whitespace-nowrap">
                            <Badge
                              size="sm"
                              color={statusBadgeColor(item.status)}
                            >
                              {statusLabel(item.status)}
                            </Badge>
                          </TableCell>

                          {/* Source */}
                          <TableCell className="px-6 py-4 whitespace-nowrap">
                            <Badge
                              size="sm"
                              color={sourceBadgeColor(item.source)}
                            >
                              {sourceLabel(item.source)}
                            </Badge>
                          </TableCell>

                          {/* Tracking # */}
                          <TableCell className="px-6 py-4 whitespace-nowrap text-gray-700 text-theme-sm dark:text-gray-400">
                            {item._tracker?.filing_id ? (
                              <button
                                onClick={() => setDetailFiling(item._tracker!)}
                                className="font-medium text-brand-500 hover:underline"
                              >
                                {item._tracker.filing_id}
                              </button>
                            ) : (
                              "—"
                            )}
                          </TableCell>

                          {/* Last Updated */}
                          <TableCell className="px-6 py-4 whitespace-nowrap text-gray-700 text-theme-sm dark:text-gray-400">
                            {formatDate(item.last_updated)}
                          </TableCell>

                          {/* Action */}
                          <TableCell className="px-6 py-4 whitespace-nowrap">
                            {item.source === "draft" ? (
                              <div className="flex items-center gap-3">
                                {item.draft_route ? (
                                  <button
                                    onClick={() => navigate(item.draft_route!)}
                                    className="text-sm font-medium text-brand-500 hover:text-brand-600 dark:text-brand-400 dark:hover:text-brand-300 transition-colors"
                                  >
                                    Continue
                                  </button>
                                ) : (
                                  <span className="text-sm text-gray-400 dark:text-gray-500">—</span>
                                )}
                                <button
                                  onClick={() =>
                                    item.draft_id && setMoveFilingId(item.draft_id)
                                  }
                                  className="text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                                  title="Move to workspace"
                                >
                                  Move
                                </button>
                                <button
                                  onClick={() =>
                                    item.delete_id &&
                                    setPendingDelete({ id: item.delete_id, formType: item.form_type })
                                  }
                                  className="text-sm font-medium text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                                  title="Delete draft"
                                >
                                  Delete
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() =>
                                  item._tracker &&
                                  setDetailFiling(item._tracker)
                                }
                                className="text-sm font-medium text-brand-500 hover:text-brand-600 dark:text-brand-400 dark:hover:text-brand-300 transition-colors"
                              >
                                View
                              </button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-6 py-4 border-t border-gray-200 dark:border-white/[0.05]">
                  <div className="flex items-center justify-between">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        setCurrentPage((p) => Math.max(1, p - 1))
                      }
                      disabled={currentPage === 1}
                    >
                      <svg
                        className="fill-current"
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M2.58301 9.99868C2.58272 10.1909 2.65588 10.3833 2.80249 10.53L7.79915 15.5301C8.09194 15.8231 8.56682 15.8233 8.85981 15.5305C9.15281 15.2377 9.15297 14.7629 8.86018 14.4699L5.14009 10.7472L16.6675 10.7472C17.0817 10.7472 17.4175 10.4114 17.4175 9.99715C17.4175 9.58294 17.0817 9.24715 16.6675 9.24715L5.14554 9.24715L8.86017 5.53016C9.15297 5.23717 9.15282 4.7623 8.85983 4.4695C8.56684 4.1767 8.09197 4.17685 7.79917 4.46984L2.84167 9.43049C2.68321 9.568 2.58301 9.77087 2.58301 9.99715C2.58301 9.99766 2.58301 9.99817 2.58301 9.99868Z"
                          fill=""
                        />
                      </svg>
                      <span className="hidden sm:inline">Previous</span>
                    </Button>

                    <div className="flex items-center gap-1">
                      {(() => {
                        const pages: (number | string)[] = [];
                        if (totalPages <= 7) {
                          for (let i = 1; i <= totalPages; i++) pages.push(i);
                        } else {
                          pages.push(1);
                          if (currentPage > 3) pages.push("...");
                          for (
                            let i = Math.max(2, currentPage - 1);
                            i <= Math.min(totalPages - 1, currentPage + 1);
                            i++
                          ) {
                            pages.push(i);
                          }
                          if (currentPage < totalPages - 2) pages.push("...");
                          pages.push(totalPages);
                        }
                        return pages.map((page, idx) =>
                          page === "..." ? (
                            <span
                              key={`ellipsis-${idx}`}
                              className="w-10 h-10 flex items-center justify-center text-gray-400"
                            >
                              ...
                            </span>
                          ) : (
                            <button
                              key={page}
                              onClick={() => setCurrentPage(page as number)}
                              className={`w-10 h-10 rounded-lg font-medium transition-colors ${
                                currentPage === page
                                  ? "bg-brand-500 text-white"
                                  : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5"
                              }`}
                            >
                              {page}
                            </button>
                          )
                        );
                      })()}
                    </div>

                    <Button
                      onClick={() =>
                        setCurrentPage((p) => Math.min(totalPages, p + 1))
                      }
                      size="sm"
                      variant="outline"
                      disabled={currentPage === totalPages}
                    >
                      <span className="hidden sm:inline">Next</span>
                      <svg
                        className="fill-current"
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M17.4175 9.9986C17.4178 10.1909 17.3446 10.3832 17.198 10.53L12.2013 15.5301C11.9085 15.8231 11.4337 15.8233 11.1407 15.5305C10.8477 15.2377 10.8475 14.7629 11.1403 14.4699L14.8604 10.7472L3.33301 10.7472C2.91879 10.7472 2.58301 10.4114 2.58301 9.99715C2.58301 9.58294 2.91879 9.24715 3.33301 9.24715L14.8549 9.24715L11.1403 5.53016C10.8475 5.23717 10.8477 4.7623 11.1407 4.4695C11.4336 4.1767 11.9085 4.17685 12.2013 4.46984L17.1588 9.43049C17.3173 9.568 17.4175 9.77087 17.4175 9.99715C17.4175 9.99763 17.4175 9.99812 17.4175 9.9986Z"
                          fill=""
                        />
                      </svg>
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
}
